import type { ExperienceDraft } from '@/lib/experience-draft';
import { draftAsContent } from '@/lib/experience-draft';
import { newBlock } from '@/lib/page-editor';
import type { BlockType, ExperienceBlockDraft, RichText } from '@matter_extractor/shared';
import {
  EMPTY_RICH_TEXT,
  EXPERIENCE_BUTTON_LABEL_MAX,
  EXPERIENCE_CAPTION_MAX,
  EXPERIENCE_CHECKBOX_LABEL_MAX,
  EXPERIENCE_INPUT_LABEL_MAX,
  EXPERIENCE_NOTE_QUESTION_MAX,
  EXPERIENCE_SELECTION_QUESTION_MAX,
  EXPERIENCE_TEXT_MAX,
  experienceTextSitesOf,
  experienceVariablesOf,
  richTextPlain,
} from '@matter_extractor/shared';

/**
 * Changer le TYPE d'un bloc sans le perdre (refonte modulaire §9.3 ; grill du
 * 2026-09-23 ; issue #136).
 *
 * « Il faudrait qu'on puisse changer le type d'un bloc » - parce qu'on se
 * trompe, et que supprimer puis reposer coûte le placement, l'animation et le
 * visuel qu'on venait de régler.
 *
 * ## Le bloc GARDE SON IDENTIFIANT, et c'est le point entier
 *
 * Un jeton `{{champ:<id>}}` d'une autre page pointe l'ID d'un bloc (§8.6). Un
 * Input converti en Checkbox garde donc ses citations INTACTES - les deux
 * déclarent un `variableName`, la source continue de produire, et rien à
 * réparer ailleurs. C'est ce qui sépare une conversion d'une suppression suivie
 * d'une pose : la seconde casse toujours, la première presque jamais.
 *
 * « Presque » : convertir vers un type qui ne déclare AUCUN nom - Texte, Image,
 * Son, Lien, Copiable, Noyau - détruit la source, et les jetons qui la citaient
 * tombent. L'inspecteur prévient par un `Callout` quand le bloc est cité, et la
 * boîte d'enregistrement les nomme ensuite comme pour n'importe quel jeton
 * pendant - c'est le même régime qu'une suppression, sauf qu'on le dit avant.
 *
 * ## Ce qui traverse : les MOTS, pas le champ qui les porte
 *
 * Chaque type range son texte ailleurs - `text` riche pour un Texte, `text`
 * brut pour un Copiable, `label` pour les quatre autres, `caption` pour une
 * Image - donc la conversion ne peut pas copier un champ : elle extrait les
 * MOTS d'un côté et les repose de l'autre, tronqués au plafond du type visé.
 * Un paragraphe de 400 caractères devenu Bouton garde ses soixante premiers ;
 * les perdre en silence serait pire, les refuser empêcherait le geste.
 *
 * Un Son et un Noyau sont les deux types sans aucun mot : ils les laissent
 * tomber, et c'est écrit ici plutôt que découvert. Un Noyau ne porte pas non
 * plus de réglage convertible : son `mode` repart au premier choix.
 *
 * ## La Sélection et la Note (#176) : ce qui traverse, et ce qui ne traverse pas
 *
 * Leur QUESTION joue le rôle d'un libellé : elle traverse comme lui, et le nom de
 * variable aussi - un Input converti en Note garde ses citations `{{champ:<id>}}`
 * intactes, puisque les quatre types déclarent un champ.
 *
 * Les OPTIONS d'une Sélection sont le seul contenu qui n'a de sens nulle part
 * ailleurs : une Sélection convertie vers un autre type les laisse tomber, et
 * une Sélection née d'une conversion repart avec les deux options VIDES que
 * `newBlock` lui donne. Les transformer en autre chose - un Texte en liste, des
 * lignes en options - déciderait à la place de l'opérateur d'une structure que
 * rien ne dit, et l'écrire à moitié est pire que le dire : l'enregistrement
 * refuse déjà des options vides (`selection-options`), donc le bloc converti se
 * signale de lui-même jusqu'à ce qu'elles soient écrites.
 *
 * La case « Obligatoire » est la seule traversée, et seulement entre la
 * Sélection et la Note, qui la portent toutes deux avec le même sens : « une page
 * ne se quitte pas sans réponse ». Elle ne traverse pas depuis l'Input ni la
 * Checkbox, qui la portent aussi - comme avant #176, personne ne la leur
 * a jamais fait traverser, et ajouter ce comportement ici changerait deux
 * conversions que rien ne demandait.
 *
 * ## Ce qui ne traverse JAMAIS
 *
 * L'`assetId` d'une Image ou d'un Son : la médiathèque est filtrée par `kind`,
 * donc un fichier son sur un bloc image nommerait un fichier que le sélecteur
 * ne montre pas. Et l'`url` d'un Lien, qui n'a d'équivalent nulle part.
 *
 * `animation`, `visual` et les CONDITIONS d'affichage survivent toujours - ce
 * sont des réglages du bloc en tant qu'objet de la page, pas de son contenu. Un
 * Son garde l'animation qu'il portait : il l'ignore au jeu (§9.3), mais la
 * remettre à `none` ferait perdre le réglage à qui reconvertit vers un type
 * animé.
 *
 * Les conditions ne sont pas listées par l'issue, qui est antérieure à #150, et
 * elles sont gardées pour la même raison que les deux autres : trois conditions
 * posées une par une et effacées par un changement de type seraient une perte
 * silencieuse. Converti vers une SORTIE - un Bouton, un Noyau en mode choix -
 * le bloc arrive alors avec des conditions que l'enregistrement refuse
 * (`condition-on-exit`) ; c'est un état que l'éditeur sait déjà dire, et son
 * inspecteur offre « Retirer les conditions » en face. Les effacer ici
 * déciderait à la place de l'opérateur, et sans le dire.
 */

/** Les mots d'un bloc, quel que soit le champ qui les range. */
export function blockWords(block: ExperienceBlockDraft): string {
  switch (block.type) {
    case 'text':
      return richTextPlain(block.text);
    case 'copyable':
      return block.text;
    case 'button':
    case 'checkbox':
    case 'input':
    case 'link':
      return block.label;
    // The question of a Sélection and of a Note plays the part of a label (#176).
    case 'selection':
    case 'note':
      return block.question;
    case 'image':
      return block.caption;
    case 'sound':
    case 'noyau':
      return '';
  }
}

/** Le nom de variable qu'un bloc déclare, ou la chaîne vide s'il n'en déclare pas. */
export function blockVariableName(block: ExperienceBlockDraft): string {
  return 'variableName' in block ? block.variableName : '';
}

/** « Obligatoire » d'une Sélection ou d'une Note - les deux seules qui le font traverser. */
function blockRequired(block: ExperienceBlockDraft): boolean | null {
  return block.type === 'selection' || block.type === 'note' ? block.required : null;
}

/** Le plafond du champ qui porte les mots, type par type - voir la tête. */
const WORDS_MAX: Readonly<Record<BlockType, number>> = {
  text: EXPERIENCE_TEXT_MAX,
  copyable: EXPERIENCE_TEXT_MAX,
  button: EXPERIENCE_BUTTON_LABEL_MAX,
  link: EXPERIENCE_BUTTON_LABEL_MAX,
  input: EXPERIENCE_INPUT_LABEL_MAX,
  checkbox: EXPERIENCE_CHECKBOX_LABEL_MAX,
  image: EXPERIENCE_CAPTION_MAX,
  sound: 0,
  noyau: 0,
  selection: EXPERIENCE_SELECTION_QUESTION_MAX,
  note: EXPERIENCE_NOTE_QUESTION_MAX,
};

/** Une ligne de texte riche depuis des mots - l'inverse de `richTextPlain`. */
function oneParagraph(words: string): RichText {
  if (words === '') return EMPTY_RICH_TEXT;
  return {
    nodes: [
      { type: 'paragraph', children: [{ type: 'text', text: words, marks: [], color: null }] },
    ],
  };
}

/**
 * Le bloc, converti - même identifiant, mêmes réglages, mots reposés.
 *
 * Le `switch` porte sur le type VISÉ et construit chaque variante en entier
 * plutôt que d'étaler un bloc neuf : `newBlock` rend une union, et un étalement
 * en élargirait le discriminant, si bien que TypeScript cesserait de vérifier
 * qu'une variante porte bien tous ses champs.
 */
export function convertBlock(block: ExperienceBlockDraft, type: BlockType): ExperienceBlockDraft {
  if (block.type === type) return block;
  const words = blockWords(block).slice(0, WORDS_MAX[type]);
  const variableName = blockVariableName(block);
  const fresh = newBlock(type);
  const keep = {
    id: block.id,
    animation: block.animation,
    visual: block.visual,
    conditions: block.conditions,
  } as const;

  switch (fresh.type) {
    case 'text':
      return { ...fresh, ...keep, text: oneParagraph(words) };
    case 'copyable':
      return { ...fresh, ...keep, text: words };
    case 'link':
      return { ...fresh, ...keep, label: words };
    case 'button':
      return { ...fresh, ...keep, label: words, variableName };
    case 'checkbox':
      return { ...fresh, ...keep, label: words, variableName };
    case 'input':
      return { ...fresh, ...keep, label: words, variableName };
    case 'image':
      return { ...fresh, ...keep, caption: words };
    // The options of a converted-away Sélection are dropped and a Sélection born
    // of a conversion keeps the two empty ones `newBlock` gives it - see the head.
    case 'selection':
      return {
        ...fresh,
        ...keep,
        question: words,
        variableName,
        required: blockRequired(block) ?? fresh.required,
      };
    case 'note':
      return {
        ...fresh,
        ...keep,
        question: words,
        variableName,
        required: blockRequired(block) ?? fresh.required,
      };
    case 'sound':
    case 'noyau':
      return { ...fresh, ...keep };
  }
}

/**
 * Les types qui DÉCLARENT un champ (quatre depuis #176) - ce qu'un `{{champ:<id>}}` peut nommer.
 *
 * Le Bouton n'en est pas, et ce n'est pas un oubli : il alimente le CHOIX de sa
 * page, adressé `{{choix:<pageId>}}` et non par son propre identifiant (§8.6).
 * Le convertir ne fait donc tomber aucune citation de bloc ; il peut priver la
 * page de son choix, ce qui est une autre phrase, dite par un autre endroit -
 * `experienceProblems` la relève à l'enregistrement.
 */
export function declaresField(type: BlockType): boolean {
  return type === 'input' || type === 'checkbox' || type === 'selection' || type === 'note';
}

/**
 * Combien de fois ce bloc est CITÉ dans l'expérience entière (§8.6 ; #136).
 *
 * Deux sortes de citations, et les deux comptent parce que les deux tombent
 * ensemble : un jeton `{{champ:<id>}}` posé dans un texte, et une condition
 * d'affichage qui nomme le bloc par structure plutôt que par jeton. La seconde
 * n'est pas un site de texte - `experienceTextSitesOf` le dit en toutes lettres
 * - donc il faut parcourir les conditions à part, comme `experienceProblems`
 * le fait de son côté.
 *
 * C'est un COMPTE et non un booléen : l'avertissement en dit le nombre, et
 * « une citation » plutôt que « des citations » est la différence entre une
 * phrase qu'on croit et une phrase qu'on relit.
 */
export function blockCitationCount(draft: ExperienceDraft, blockId: string): number {
  const content = draftAsContent(draft);
  const inTexts = experienceTextSitesOf(content).reduce(
    (count, site) =>
      count +
      experienceVariablesOf(site.text).filter(
        (variable) => variable.kind === 'field' && variable.id === blockId,
      ).length,
    0,
  );
  const inConditions = (content.pages ?? []).reduce(
    (count, page) =>
      count +
      page.temps.reduce(
        (perPage, temps) =>
          perPage +
          temps.blocks.reduce(
            (perTemps, block) =>
              perTemps +
              block.conditions.filter(
                (condition) =>
                  (condition.kind === 'checkbox' || condition.kind === 'input') &&
                  condition.blockId === blockId,
              ).length,
            0,
          ),
        0,
      ),
    0,
  );
  return inTexts + inConditions;
}
